import { makeStyles } from '@fluentui/react-components';
import { useRef } from 'react';
import { Header } from './Header';
import { NavRail } from './NavRail';
import { Footer } from './Footer';
import { CommandPalette } from './CommandPalette';
import { usePullToRefresh, PULL_THRESHOLD } from '../../hooks/usePullToRefresh';

const useStyles = makeStyles({
  layout: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    width: '100vw',
    overflow: 'hidden',
  },
  main: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    width: '100%',
  },
  // Content column: page scroll area + footer. The footer lives here (not
  // full-width) so it starts after the rail and never underlaps it.
  contentColumn: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
    minWidth: 0,
  },
  content: {
    flex: 1,
    overflow: 'auto',
    backgroundColor: 'var(--hts-app-bg)',
    width: '100%',
    position: 'relative',
  },
  contentWithPadding: {
    padding: '24px',
  },
  // Pull-to-refresh indicator rail: a zero-height sticky strip at the top
  // of the scroller so the ring overlays content without layout shift.
  ptrRail: {
    position: 'sticky',
    top: 0,
    display: 'flex',
    justifyContent: 'center',
    height: '0',
    zIndex: 5,
    pointerEvents: 'none',
  },
  ptrRing: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    // Ring mask: opaque-alpha stop via black (mask never uses ink tokens).
    WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2.5px))',
    mask: 'radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2.5px))',
    animation: 'hts-ptr-spin 800ms linear infinite',
  },
});

interface LayoutProps {
  children: React.ReactNode;
  hideNavigation?: boolean;
  noPadding?: boolean;
  /**
   * Immersive tool pages (asset viewer, survey canvas): drop the 64px header
   * entirely — screen real estate is precious on a field iPad — but keep the
   * footer so users can still Report Bug / Request Feature from context.
   * Implies the rail-free layout of `hideNavigation`; each immersive page
   * provides its own back affordance, so the hamburger isn't needed.
   */
  immersive?: boolean;
}

/**
 * App shell (HTS app-shell standard): 64px header on top; below it the 72px
 * nav rail beside the content column (page + footer). `hideNavigation` is
 * kept for the full-screen tool pages (install/design) — they get the header
 * (with hamburger) but no rail or footer. `immersive` goes further for the
 * annotation/survey surfaces: no header either, footer kept.
 *
 * A skip link (`.hts-skip-link`) is the first focusable element so keyboard
 * users can jump past the 64px header + 72px rail. SS was the only hub of
 * the four missing this — added 2026-07-29 on `button-align-test`.
 */
export const Layout: React.FC<LayoutProps> = ({
  children,
  hideNavigation = false,
  noPadding = false,
  immersive = false,
}) => {
  const styles = useStyles();
  const contentRef = useRef<HTMLElement | null>(null);
  // Pull-to-refresh on the shared main scroller (one gesture app-wide, the
  // portal app's pattern). We have no query cache to invalidate, so the
  // refresh is a full document reload — session (cookie), route, and drafts
  // (localStorage) all survive; when a query layer lands, swap this for
  // targeted invalidation like the portal's queryClient.invalidateQueries().
  const { pullDist, refreshing } = usePullToRefresh(contentRef, () => {
    window.location.reload();
  });

  const progress = refreshing ? 1 : Math.min(pullDist / PULL_THRESHOLD, 1);

  return (
    <div className={styles.layout}>
      <a href="#main" className="hts-skip-link">Skip to main content</a>
      {!immersive && <Header compact={hideNavigation} />}
      <div className={styles.main}>
        {!hideNavigation && <NavRail />}
        <div className={styles.contentColumn}>
          <main
            id="main"
            ref={contentRef}
            className={`${styles.content} ${!hideNavigation && !noPadding ? styles.contentWithPadding : ''}`}
          >
            {(pullDist > 0 || refreshing) && (
              <div className={styles.ptrRail} aria-hidden={!refreshing}>
                <div
                  className={styles.ptrRing}
                  style={{
                    background: `conic-gradient(var(--hts-accent-ink) ${progress * 360}deg, rgba(128, 128, 128, 0.35) 0deg)`,
                    marginTop: `calc(10px + ${(refreshing ? 0 : pullDist * 0.4).toFixed(1)}px)`,
                    opacity: refreshing ? 1 : 0.35 + progress * 0.65,
                    transform: `scale(${refreshing ? 1 : 0.7 + progress * 0.3})`,
                    animationPlayState: refreshing ? 'running' : 'paused',
                    transition: pullDist === 0 && !refreshing ? 'margin-top 220ms ease-out, opacity 220ms ease-out, transform 220ms ease-out' : undefined,
                  }}
                />
              </div>
            )}
            {children}
          </main>
          {(!hideNavigation || immersive) && <Footer />}
        </div>
      </div>
      <CommandPalette />
    </div>
  );
};

export default Layout;
